// Types for the Quick Setup wizard

export type SetupStepId =
  | 'opportunity_boards'
  | 'job_boards'
  | 'marketing_photos'
  | 'knowledge_graph'
  | 'services'
  | 'gbp_connection'
  | 'team_invitations';

export interface SetupStep {
  id: SetupStepId;
  title: string;
  shortTitle: string;
  description: string;
  order: number;
  videoUrl?: string;
}

export interface SetupProgress {
  id: number;

  // Step completion status
  opportunity_boards_completed: boolean;
  opportunity_boards_completed_at: string | null;

  job_boards_completed: boolean;
  job_boards_completed_at: string | null;

  marketing_photos_completed: boolean;
  marketing_photos_completed_at: string | null;

  knowledge_graph_completed: boolean;
  knowledge_graph_completed_at: string | null;

  services_completed: boolean;
  services_completed_at: string | null;

  gbp_connection_completed: boolean;
  gbp_connection_completed_at: string | null;

  team_invitations_completed: boolean;
  team_invitations_completed_at: string | null;

  // Wizard state
  current_step: SetupStepId;
  wizard_completed: boolean;
  wizard_completed_at: string | null;
  wizard_dismissed: boolean;
  wizard_dismissed_at: string | null;

  // Computed fields from backend
  completed_steps_count: number;
  progress_percentage: number;
  next_incomplete_step: SetupStepId | null;

  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface SetupProgressUpdate {
  step: SetupStepId;
  completed?: boolean;
  current_step?: SetupStepId;
}

export const SETUP_STEPS: SetupStep[] = [
  {
    id: 'opportunity_boards',
    title: 'Set Up Your Sales Pipeline',
    shortTitle: 'Sales Pipeline',
    description:
      'Opportunity boards help you track leads as they move through your sales process. Create boards for different types of work (residential, commercial, insurance) and define stages that match how you sell.',
    order: 1,
  },
  {
    id: 'job_boards',
    title: 'Configure Your Job Pipeline',
    shortTitle: 'Job Pipeline',
    description:
      'Job boards track active projects from start to completion. Define the stages your jobs go through - from scheduling to final inspection.',
    order: 2,
  },
  {
    id: 'marketing_photos',
    title: 'Add Photos of Your Work',
    shortTitle: 'Marketing Photos',
    description:
      'Quality photos of your completed projects help win new customers. Upload photos to use for your Google Business Profile posts, website, and marketing materials.',
    order: 3,
  },
  {
    id: 'knowledge_graph',
    title: 'Complete Your Company Profile',
    shortTitle: 'Company Profile',
    description:
      'A complete company profile powers your SEO, AI-generated marketing content, and professional communications. The more you fill in, the better ApexOS can help market your business.',
    order: 4,
  },
  {
    id: 'services',
    title: 'Select Your Services',
    shortTitle: 'Services',
    description:
      'Tell us what services your company offers. This helps ApexOS generate better marketing content, improve your SEO, and tailor your experience.',
    order: 5,
  },
  {
    id: 'gbp_connection',
    title: 'Connect Google Business Profile',
    shortTitle: 'Google Business',
    description:
      'Connect your Google Business Profile to automatically post updates, track reviews, and manage your online presence directly from ApexOS.',
    order: 6,
  },
  {
    id: 'team_invitations',
    title: 'Invite Your Team',
    shortTitle: 'Team',
    description:
      'Get your team on ApexOS to collaborate on jobs, manage contacts, and track everything together. Team members will receive an email invitation to set up their account.',
    order: 7,
  },
];

export const getStepById = (id: SetupStepId): SetupStep | undefined => {
  return SETUP_STEPS.find((step) => step.id === id);
};

export const getStepIndex = (id: SetupStepId): number => {
  return SETUP_STEPS.findIndex((step) => step.id === id);
};

export const getNextStep = (currentId: SetupStepId): SetupStep | null => {
  const currentIndex = getStepIndex(currentId);
  if (currentIndex === -1 || currentIndex >= SETUP_STEPS.length - 1) {
    return null;
  }
  return SETUP_STEPS[currentIndex + 1];
};

export const getPreviousStep = (currentId: SetupStepId): SetupStep | null => {
  const currentIndex = getStepIndex(currentId);
  if (currentIndex <= 0) {
    return null;
  }
  return SETUP_STEPS[currentIndex - 1];
};

export const isStepCompleted = (
  progress: SetupProgress,
  stepId: SetupStepId
): boolean => {
  const field = `${stepId}_completed` as keyof SetupProgress;
  return progress[field] as boolean;
};
